import styles from '../assets/styles/NewContainerFlow.module.css';
import { Eyebrow } from '../components/ds';
import { ReactComponent as Spinner } from '../assets/images/status-spinner.svg';
import {ReactComponent as SearchIcon} from "../assets/images/search-icon.svg";
import { ContainerCreationLimits, ContainerFeature, GitHubRepository, Plan, Subscription } from '@back4app2/sdk/lib/Back4app2';
import { App, AuthenticationError, AuthorizationError, NetworkError, GitHubCodeError, GitHubInstallationIdError, DuplicatedError, NotFoundError, BlockedResourceError, UserVerificationPendingError, CreditCardValidationRequiredError, InvalidInputError, User } from '@back4app2/sdk';
import back4app2 from '../back4app2';

import { useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import IntegrateGithub, { filterRepositories } from '../components/IntegrateGithub/IntegrateGithub';
import ConfirmRepository from '../components/ConfirmRepository/ConfirmRepository';
import Button from '../components/Button';

import { ReactComponent as GithubIcon } from "../assets/images/github-logo.svg";
import { ReactComponent as B4AIconSVG } from "../assets/images/b4a-sqaure-glow-logo.svg";
import { ContainerType } from '../types/containerTypes';

import { DsButton } from '../components/ds';
import CreateContainerForm from './CreateContainerForm';
import { containerTotal, formatMoney, FeaturesCard, InstanceSizePanel } from './PlanAndExtras';
import { AlertIcon, ArrowLeftIcon } from '../components/ds/icons';
import SourceSelector, { AppSource } from '../components/SourceSelector/SourceSelector';
import ContainerImageSource, { ContainerImageValue, INITIAL_CONTAINER_IMAGE } from '../components/ContainerImageSource/ContainerImageSource';

import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from '../settings';
import { AmplitudeEvent, trackEvent } from '../utils/amplitude';
import { openGithubPermissions } from '../utils/githubPopup';
import useContainerPurchase, { PurchaseOutcome } from '../hooks/useContainerPurchase';
import CreditCardValidation from '../components/CreditCardValidation';
import useIsMobile from '../hooks/useIsMobile';

interface NewContainerFlowState {
  step: number;
  gitHubRepositories: GitHubRepository[] | undefined,
  
  isLoadingRepository: boolean, // do we really need it?
  loadingRepositoryErrorMsg: string | undefined,
  
  isImporting: boolean,
  importingRepoError: string | undefined;
  isImportingSuccess: boolean;
  showCreditCardRequired: boolean;
  
  selectedRepository: GitHubRepository | undefined,
  /** Chosen beside the repository, before anything is read from it. */
  repositoryBranch: string,
  repositoryRootDir: string,
  isCreatingApp: boolean;
  creatingAppErrorMessage: string;
  plans: Plan[];
  features: ContainerFeature[];
  isLoadingCatalog: boolean;
  catalogErrorMessage: string;
  selectedPlanId: string | undefined;
  selectedFeatureIds: string[];
  /** The app that was just created, when its first build is waiting on the
   * hold screen. Null on every other path — a clean project goes straight to
   * the overview. */
  createdApp: App | undefined;
  /**
   * The app exists and the payment did not go through.
   *
   * The wizard STAYS here when this is set. Landing the customer on an app
   * page that only says "Pending Payment" turns a decision they were in the
   * middle of into a puzzle they have to solve somewhere else — and the two
   * things they might want to do next (try again, or take it on Free) are both
   * cheaper to offer right where they are.
   */
  paymentDecision: PaymentDecision | undefined;
  isResolvingPayment: boolean;
  paymentDecisionError: string;
};

/** Why the money did not move — the two cases read differently to the person. */
type PaymentDecision = {
  appId: string;
  planId: string;
  /** CLOSED: they shut the checkout. FAILED: the charge was refused. */
  reason: 'CLOSED' | 'FAILED';
  message?: string;
  /**
   * Whether the app already landed on Free.
   *
   * It normally has: an app is never LEFT on a plan it did not pay for. False
   * only when the fallback itself was refused — the account's free allowance is
   * full, or the repository is not eligible for Free — and then the app is
   * still pending and the only way out is the payment.
   */
  movedToFree: boolean;
  /** The server's reason for refusing the fallback, when it did. */
  fallbackError?: string;
};

const INITIAL_STATE:  NewContainerFlowState = {
  step: 0,
  
  gitHubRepositories: undefined,
  isLoadingRepository: true,
  loadingRepositoryErrorMsg: undefined,
  
  isImporting: false,
  importingRepoError: undefined,
  isImportingSuccess: false,
  showCreditCardRequired: false,

  selectedRepository: undefined,
  repositoryBranch: '',
  repositoryRootDir: '',

  isCreatingApp: false,
  creatingAppErrorMessage: '',
  createdApp: undefined,

  plans: [],
  features: [],
  isLoadingCatalog: false,
  catalogErrorMessage: '',
  selectedPlanId: undefined,
  selectedFeatureIds: [],

  paymentDecision: undefined,
  isResolvingPayment: false,
  paymentDecisionError: ''
};

/**
 * Two steps, then a hold. Not counted on screen: with one screen to fill in and
 * one button to press, a rail saying "step 2 of 2" is furniture around a form
 * that is already the whole job. It earned its place when the flow was three
 * screens and you could not tell how much was left.
 *
 * It was three: source, build settings, then plan & extras on a screen of its
 * own. The middle step's button therefore said "Next" and created nothing, and
 * Back from the plan screen had to keep a whole form alive to avoid emptying
 * it. The plan is part of configuring the app — it is now the last block of
 * the same step, under the same Create.
 *
 * The hold is not a step and is not counted: by the time it renders the app
 * exists and there is nothing left to ask.
 */
const HOLD_STEP = 2;

enum ContainerFlowActionType {
  Reset,
  StartImportingRepository,
  ImportingRepoSuccess,
  ImportingRepoError,
  SetSelectedRepository,
  ClearSelectedRepository,
  ConfirmRepository,
  SetCurrentStep,
  SetGithubRepositories,
  SetFetchGithubRepositoriesError,
  SetContainerName,
  StartCreatingApp,
  FinishCreatingApp,
  ShowCardRequired,
  HoldFirstBuild,
  StartLoadingCatalog,
  FinishLoadingCatalog,
  SelectPlan,
  ToggleFeature,
  AskPaymentDecision,
  StartResolvingPayment,
  FinishResolvingPayment,
}

const reset = () => ({ type: ContainerFlowActionType.Reset } as const);
const updateCurrentStep = (value: number) => ({ type: ContainerFlowActionType.SetCurrentStep, payload: value } as const);

const setGithubRepositories = (value: GitHubRepository[] | undefined) => ({ type: ContainerFlowActionType.SetGithubRepositories, payload: value } as const);
const setFetchGithubRepositoriesError = (message: string) => ({ type: ContainerFlowActionType.SetFetchGithubRepositoriesError, payload: message } as const);

const startImportingRepository = () => ({ type: ContainerFlowActionType.StartImportingRepository } as const);
const importRepositorySuccess = () => ({ type: ContainerFlowActionType.ImportingRepoSuccess } as const);
const importRepositoryError = (message: string) => ({ type: ContainerFlowActionType.ImportingRepoError, payload: message } as const);
const setShowCreditCardRequired = (message: string) => ({ type: ContainerFlowActionType.ShowCardRequired, payload: message } as const);

const askPaymentDecision = (value: PaymentDecision) => ({ type: ContainerFlowActionType.AskPaymentDecision, payload: value } as const);
const startResolvingPayment = () => ({ type: ContainerFlowActionType.StartResolvingPayment } as const);
const finishResolvingPayment = (message?: string) => ({ type: ContainerFlowActionType.FinishResolvingPayment, payload: message } as const);

const startLoadingCatalog = () => ({ type: ContainerFlowActionType.StartLoadingCatalog } as const);
const finishLoadingCatalog = (plans: Plan[], features: ContainerFeature[], errorMessage?: string) => ({
  type: ContainerFlowActionType.FinishLoadingCatalog,
  payload: { plans, features, errorMessage }
} as const);
const selectPlan = (planId: string) => ({ type: ContainerFlowActionType.SelectPlan, payload: planId } as const);
const toggleFeature = (featureId: string) => ({ type: ContainerFlowActionType.ToggleFeature, payload: featureId } as const);

const setSelectedRepository = (repo: GitHubRepository) => ({ type: ContainerFlowActionType.SetSelectedRepository, payload: repo } as const);

const clearSelectedRepository = () => ({ type: ContainerFlowActionType.ClearSelectedRepository } as const);

const confirmRepository = (branchName: string, rootDirectoryPath: string) => ({
  type: ContainerFlowActionType.ConfirmRepository,
  payload: { branchName, rootDirectoryPath },
} as const);

const setContainerName = (value: string) => ({
  type: ContainerFlowActionType.SetContainerName,
  payload: {
    value
  }
} as const);

const startCreatingApp = () => ({
  type: ContainerFlowActionType.StartCreatingApp,
} as const);

// The app was created and its first build is waiting on the pre-deploy
// screen. Not an error and not a failure to create: the app exists, and this
// is the last step of making it.
const holdFirstBuild = (app: App) => ({
  type: ContainerFlowActionType.HoldFirstBuild,
  payload: app
} as const);

const finishCreatingApp = (errorMessage?: string) => ({
  type: ContainerFlowActionType.FinishCreatingApp,
  payload: {
    errorMessage
  }
} as const);

type NewContainerFlowAction = 
  | ReturnType<typeof reset> 
  | ReturnType<typeof startImportingRepository> 
  | ReturnType<typeof importRepositorySuccess> 
  | ReturnType<typeof importRepositoryError> 
  | ReturnType<typeof setSelectedRepository>
  | ReturnType<typeof clearSelectedRepository>
  | ReturnType<typeof confirmRepository> 
  | ReturnType<typeof updateCurrentStep>
  | ReturnType<typeof setGithubRepositories>
  | ReturnType<typeof setFetchGithubRepositoriesError>
  | ReturnType<typeof setContainerName>
  | ReturnType<typeof startCreatingApp>
  | ReturnType<typeof finishCreatingApp>
  | ReturnType<typeof holdFirstBuild>
  | ReturnType<typeof setShowCreditCardRequired>
  | ReturnType<typeof startLoadingCatalog>
  | ReturnType<typeof finishLoadingCatalog>
  | ReturnType<typeof selectPlan>
  | ReturnType<typeof toggleFeature>
  | ReturnType<typeof askPaymentDecision>
  | ReturnType<typeof startResolvingPayment>
  | ReturnType<typeof finishResolvingPayment>;

/** The paid plan a free choice turns into when a paid-only option is picked. */
export const cheapestPaidPlan = (plans: Plan[]): Plan | undefined =>
  plans
    .filter((plan) => plan.price > 0)
    .sort((a, b) => a.price - b.price)[0];

function reducer(state: NewContainerFlowState, action: NewContainerFlowAction): NewContainerFlowState {
  switch (action.type) {
    case ContainerFlowActionType.Reset:
      return { ...INITIAL_STATE };
    
    case ContainerFlowActionType.StartImportingRepository:
      return { ...state, isImporting: true, gitHubRepositories: undefined, importingRepoError: undefined};

    case ContainerFlowActionType.ImportingRepoSuccess:
      return { ...state, isImporting: false, isImportingSuccess: true };

    case ContainerFlowActionType.ImportingRepoError:
      return { ...state, isImporting: false, importingRepoError: action.payload, isImportingSuccess: false };

    case ContainerFlowActionType.ShowCardRequired:
      return { ...state, isImporting: false, importingRepoError: action.payload, isImportingSuccess: false, showCreditCardRequired: true };

    case ContainerFlowActionType.SetSelectedRepository:
      // No step change. Picking the repository opens the rest of the same
      // question — which branch, which folder — and Continue is what moves on.
      return {
        ...state,
        selectedRepository: action.payload,
        repositoryBranch: action.payload.defaultBranch || '',
        repositoryRootDir: '',
      };

    case ContainerFlowActionType.ClearSelectedRepository:
      return {
        ...state,
        selectedRepository: undefined,
        repositoryBranch: '',
        repositoryRootDir: '',
      };

    case ContainerFlowActionType.ConfirmRepository:
      return {
        ...state,
        repositoryBranch: action.payload.branchName,
        repositoryRootDir: action.payload.rootDirectoryPath,
        step: 1,
      };

    case ContainerFlowActionType.SetCurrentStep:
      return { ...state, step: action.payload, isCreatingApp: false, creatingAppErrorMessage: '' };

    case ContainerFlowActionType.HoldFirstBuild:
      return { ...state, step: HOLD_STEP, isCreatingApp: false, creatingAppErrorMessage: '', createdApp: action.payload };
      
    case ContainerFlowActionType.AskPaymentDecision:
      // isCreatingApp goes false: the app IS created. What is pending is the
      // money, and the card below says so in its own words.
      return { ...state, isCreatingApp: false, creatingAppErrorMessage: '', isResolvingPayment: false, paymentDecision: action.payload, paymentDecisionError: '' };

    case ContainerFlowActionType.StartResolvingPayment:
      return { ...state, isResolvingPayment: true, paymentDecisionError: '' };

    case ContainerFlowActionType.FinishResolvingPayment:
      return { ...state, isResolvingPayment: false, paymentDecisionError: action.payload || '' };

    case ContainerFlowActionType.StartLoadingCatalog:
      return { ...state, isLoadingCatalog: true, catalogErrorMessage: '' };

    case ContainerFlowActionType.FinishLoadingCatalog: {
      const { plans, features, errorMessage } = action.payload;
      return {
        ...state,
        isLoadingCatalog: false,
        plans,
        features,
        catalogErrorMessage: errorMessage || '',
        // Preselect Free: a new container starts at no cost, and paying is a
        // choice the customer makes on this screen rather than one made for
        // them. Adding a paid-only extra or auto deploy still moves the
        // selection up to a paid plan. Falls back to whatever came first.
        selectedPlanId:
          state.selectedPlanId ||
          (plans.find((plan) => plan.price === 0) || plans[0])?.id
      };
    }

    case ContainerFlowActionType.SelectPlan: {
      const planId = action.payload;
      // Extras are gated per plan, so a selection that the new plan cannot sell
      // has to drop — otherwise the summary bills something unavailable.
      const stillAvailable = state.selectedFeatureIds.filter((featureId) =>
        state.features.some((feature) => feature.id === featureId && feature.planIds.includes(planId))
      );
      // The last refusal goes with the plan it was about: it sits beside the
      // button, and "all your free apps are in use" next to a Starter total
      // would be about something no longer being bought.
      return { ...state, selectedPlanId: planId, selectedFeatureIds: stillAvailable, creatingAppErrorMessage: '' };
    }

    case ContainerFlowActionType.ToggleFeature: {
      const featureId = action.payload;

      if (state.selectedFeatureIds.includes(featureId)) {
        return {
          ...state,
          selectedFeatureIds: state.selectedFeatureIds.filter((id) => id !== featureId)
        };
      }

      // Adding an extra the chosen plan does not sell moves the app to the
      // cheapest plan that does, instead of refusing the click: the person who
      // pressed Add on the database wants the database, and the plan that
      // makes it possible is one step they would otherwise have to find.
      const feature = state.features.find((candidate) => candidate.id === featureId);
      const isSoldOnPlan = !!feature && !!state.selectedPlanId && feature.planIds.includes(state.selectedPlanId);
      const upgradeTo = feature && !isSoldOnPlan
        ? cheapestPaidPlan(state.plans.filter((plan) => feature.planIds.includes(plan.id)))
        : undefined;

      return {
        ...state,
        selectedPlanId: upgradeTo ? upgradeTo.id : state.selectedPlanId,
        selectedFeatureIds: [...state.selectedFeatureIds, featureId]
      };
    }

    case ContainerFlowActionType.SetGithubRepositories:
      return { ...state, gitHubRepositories: action.payload, isLoadingRepository: false };

    case ContainerFlowActionType.SetFetchGithubRepositoriesError:
      return { ...state, isLoadingRepository: false, loadingRepositoryErrorMsg: action.payload}
    
    // case ContainerFlowActionType.SetContainerName:
    //   return {
    //     ...state,
    //     container: {
    //       ...state.container,
    //       name: action.payload.value
    //     }
    //   }
    
    case ContainerFlowActionType.StartCreatingApp:
      return {
        ...state,
        isCreatingApp: true,
        creatingAppErrorMessage: ''
      }

    case ContainerFlowActionType.FinishCreatingApp:
      return {
        ...state,
        isCreatingApp: false,
        creatingAppErrorMessage: action.payload.errorMessage || ''
      }

    default:
      return state;
  }
}

const NewContainerFlow = () => {
  const { user } = useOutletContext<{ user: User }>();
  const [searchParams] = useSearchParams();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const { gitHubRepositories, isImporting, importingRepoError, step, loadingRepositoryErrorMsg, isCreatingApp, creatingAppErrorMessage, selectedRepository, repositoryBranch, repositoryRootDir, isLoadingRepository, isImportingSuccess, showCreditCardRequired, createdApp, plans, features, isLoadingCatalog, catalogErrorMessage, selectedPlanId, selectedFeatureIds, paymentDecision, isResolvingPayment, paymentDecisionError } = state;
  // Where this app's code comes from. Asked once, at creation: switching an
  // existing app's source is a different question with different consequences,
  // and Settings does not offer it.
  const [source, setSource] = useState<AppSource>('GITHUB');
  /**
   * One query per source, not one shared between them.
   *
   * The box moved into the panel head and started serving both sources, and
   * with a single piece of state a repository name typed on the GitHub side
   * became a Docker Hub search the moment you switched — firing a real query
   * against the registry for a string that was never meant for it.
   *
   * Keyed rather than cleared on switch, so going back to a source you were
   * already searching finds your text where you left it.
   */
  const [searchBySource, setSearchBySource] = useState<Record<AppSource, string>>({
    GITHUB: '',
    DOCKER_HUB: '',
  });
  const searchInputText = searchBySource[source];
  const setSearchInputText = (text: string) =>
    setSearchBySource((prev) => ({ ...prev, [source]: text }));

  /**
   * The names this account has already used, for the form's own name field.
   *
   * Loaded on the source step rather than with the plan catalog: the name is
   * suggested the moment the build form mounts, and picking a repository is
   * exactly the pause this query fits into. A failure is not fatal — the field
   * keeps its plain suggestion and the server still refuses a real duplicate.
   */
  const [takenAppNames, setTakenAppNames] = useState<string[]>([]);

  /**
   * The account's free allowance, from the server. Fetched with the catalog
   * because it is only needed on the same step, and only to answer one
   * question: is the Free row still an option?
   */
  const [creationLimits, setCreationLimits] = useState<ContainerCreationLimits | undefined>();

  // The purchase itself, and the checkout it opens, are the same ones the
  // pending-payment card uses. A second copy here is how the two drifted.
  const { purchase, checkoutModal } = useContainerPurchase();

  /**
   * The step's own scroller, so a new step starts at its top.
   *
   * It used to carry a second job — a latch that watched the total scroll into
   * view and made the primary button say "Review" until it had, because the
   * price lived at the foot of a long form and a fixed bar put the purchase one
   * press from the first field. The price is in the rail now, beside the button
   * from the first paint, so there is nothing left to scroll to and nothing to
   * make the button hesitate about.
   */
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isNarrowScreen = useIsMobile();

  const [containerImage, setContainerImage] = useState<ContainerImageValue>(INITIAL_CONTAINER_IMAGE);
  // The resolved image, or null while it is unresolved. Next is gated on this
  // rather than on the fields being filled in — a typo'd tag fills the fields
  // just as well as a real one.
  const navigate = useNavigate();
  
  const code = searchParams.get('code');
  const installationId = searchParams.get('installation_id');
  const setupAction = searchParams.get('setup_action');

  useEffect(() => {
    return () => { dispatch(reset()) };
  }, []);

  useEffect(() => {
    let canceled = false;

    back4app2
      .findApps()
      .then((apps) => {
        if (!canceled) {
          // Only apps this account OWNS: the unique index is on (user_id,
          // name), so a collaborator's app of the same name is not a clash.
          setTakenAppNames(apps.filter((app) => app.isOwner).map((app) => app.name));
        }
      })
      .catch((e) => {
        console.error('could not load the existing app names', e);
      });

    return () => { canceled = true; };
  }, []);

  useEffect(() => {
    let subscription: Subscription;

    // Nothing to list on the image tab, and subscribing would surface a
    // GitHub error on a screen that never mentions GitHub.
    if (step === 0 && source === 'GITHUB') {
      subscription = back4app2.subscribeToGitHubRepositories((error, snapshot) => {
        if (error) {
          if (error instanceof NetworkError) {
            console.error('network error', error);
            dispatch({ 
              type: ContainerFlowActionType.SetFetchGithubRepositoriesError, 
              payload: 'Network error when loading GitHub repositories. Please check your internet connection or try again later.'
            });
          } else if (error instanceof AuthenticationError || error instanceof AuthorizationError) {
            window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
          } else {
            console.error('unexpected error loading GitHub repositories', error);
            dispatch({ 
              type: ContainerFlowActionType.SetFetchGithubRepositoriesError, 
              payload: 'Unexpected error when loading GitHub repositories. Please try again later.'
            });
          }
  
          subscription.unsubscribe();
        } else {
          dispatch({
            type: ContainerFlowActionType.SetGithubRepositories,
            payload: snapshot
          });
        }
      });
    }

    return () => {
      if (subscription) {
        subscription.unsubscribe()
      }      
    };
  }, [step, source]);

  useEffect(() => {
    
    if (!code || !installationId || !setupAction || !['install', 'update'].includes(setupAction)) {
      if ((window.opener && window.opener !== window) || window.name === 'GithubIntegrationWindow') {
        dispatch({
          type: ContainerFlowActionType.ImportingRepoError,
          payload: 'Something went wrong! Please try again after uninstalling the app.'
        });
        new Promise<void>(res => setTimeout(() => {
          window.close();
          res();
        }, 8_000));
      }
      return;
    }
    
    dispatch({ type: ContainerFlowActionType.StartImportingRepository });

    let searchParamsChanged = false, closeWindow = true, displayError = false;
    
    (async () => {
      try {
        await back4app2.importGitHubInstallation(code, installationId);
      } catch (e) {
        displayError = true;
        if (searchParamsChanged) {
          console.error('error importing GitHub installation', e);
        } else if (e instanceof NetworkError) {
          console.error('network error', e);
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: 'Network error when importing from GitHub. Please check your internet connection or try again later.'
          });
        } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
          window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        } else if (e instanceof GitHubCodeError || e instanceof GitHubInstallationIdError) {
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: 'This GitHub installation is invalid or has expired. Please try again.'
          });
        } else if (e instanceof BlockedResourceError) {
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: e.message
          });
        }  else if (e instanceof CreditCardValidationRequiredError) {
          closeWindow = false;
          dispatch({
            type: ContainerFlowActionType.ShowCardRequired,
            payload: e.message
          });
        } else {
          console.error('unexpected error importing GitHub installation', e);
          dispatch({
            type: ContainerFlowActionType.ImportingRepoError,
            payload: 'Unexpected error when importing from GitHub. Please try again later.'
          });
        }
      }

      if (!searchParamsChanged && !displayError) {
        dispatch({ type: ContainerFlowActionType.ImportingRepoSuccess });
        window.close();
      } else {
        if (closeWindow && !searchParamsChanged) {
          await new Promise(res => setTimeout(res, 8_000));
          window.close();
        }
      }
    })();
    return () => { 
      searchParamsChanged = true;
    };
  }, [code, installationId, setupAction]);

  useEffect(() => {
    step === 0 ? trackEvent(AmplitudeEvent.AT_SELECT_REPOSITORY_PAGE) : trackEvent(AmplitudeEvent.AT_CREATE_APP_PAGE);
  }, [step]);

  // Fetched when the step opens rather than up front: the catalog is only
  // needed here, and both queries are gated on an authenticated session.
  useEffect(() => {
    if (step !== 1 || plans.length || isLoadingCatalog) {
      return;
    }

    let canceled = false;
    dispatch(startLoadingCatalog());

    (async () => {
      try {
        const [loadedPlans, loadedFeatures, loadedLimits] = await Promise.all([
          back4app2.containerCreationPlans(),
          back4app2.containerFeatures(),
          // Not fatal on its own: without it the Free row simply does not warn,
          // and the server still refuses on submit. The catalog is what the
          // step cannot do without.
          back4app2.containerCreationLimits().catch(() => undefined),
        ]);

        if (!canceled) {
          dispatch(finishLoadingCatalog(loadedPlans, loadedFeatures));
          setCreationLimits(loadedLimits);

          // Free is the default, but not for someone whose free allowance is
          // already spent: preselecting it would open the screen on a disabled
          // Create button. Only on a first visit — a plan the customer picked
          // is left alone.
          const isFreeAllowanceFull =
            typeof loadedLimits?.maxFreeApps === 'number' &&
            loadedLimits.freeAppsUsed >= loadedLimits.maxFreeApps;
          const paidPlan = cheapestPaidPlan(loadedPlans);

          if (!state.selectedPlanId && isFreeAllowanceFull && paidPlan) {
            dispatch(selectPlan(paidPlan.id));
          }
        }
      } catch (e) {
        console.error('error loading the plan catalog', e);

        if (!canceled) {
          // No plan means no informed choice, so the step cannot proceed on a
          // guess — say so instead of silently defaulting.
          dispatch(finishLoadingCatalog([], [], "We couldn't load the plans. Please try again."));
        }
      }
    })();

    return () => { canceled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const onImportRepository = (repository: GitHubRepository) => {
    // Select is now a disclosure: on the row that is already open it closes
    // it, which is what a second press on an open thing has to do.
    if (repository.id === state.selectedRepository?.id) {
      dispatch(clearSelectedRepository());

      return;
    }

    dispatch(setSelectedRepository(repository));
  }
  
  const onClickBack = () => {
    // Only the hold screen is a dead end: by then the app exists and the
    // configure step's submit would create a second one.
    if (step === HOLD_STEP) {
      return;
    }

    if (step === 0) {
      // navigate(`/apps`);
      window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`
    } else if (step > 0) {
      // Coming back to pick a source starts from the whole list. The search is
      // a narrowing for ONE act of choosing: carrying it back means landing on
      // "where does your app come from?" with most of your repositories
      // hidden, and nothing on screen explaining why only two are there — the
      // box is at the top with a word in it, but you are here to choose
      // differently, not to resume the search you already finished.
      if (state.step - 1 === 0) {
        setSearchBySource({ GITHUB: '', DOCKER_HUB: '' });
      }

      dispatch({ type: ContainerFlowActionType.SetCurrentStep, payload: state.step - 1 });
    }
  }

  const onContainerFormSubmit = async (container: ContainerType) => {
    trackEvent(AmplitudeEvent.CREATE_APP_CLICK, { source });
    await createApp(container);
  };

  /**
   * Charges for the app just created, through the shared purchase flow.
   *
   * Answers WHY when the money did not move. "Refused" and "you closed it" are
   * the same failure to a boolean and two different sentences to a person — and
   * only one of them is worth an apology.
   */
  const chargeForApp = async (appId: string, planId: string): Promise<PurchaseOutcome> => {
    const plan = plans.find((candidate) => candidate.id === planId);

    return await purchase({
      appId,
      planId,
      featureIds: selectedFeatureIds,
      user,
      summary: {
        eyebrow: plan ? `deploy on ${plan.name}` : 'deploy',
        planName: plan?.name || 'Your app',
        planPrice: plan?.price || 0,
        lines: features
          .filter((feature) => selectedFeatureIds.includes(feature.id))
          .map((feature) => ({ id: feature.id, name: feature.name, price: feature.price })),
        // The app already exists. Saying so keeps closing the checkout from
        // reading as throwing away what was configured.
        note: 'Your app is already created. It starts building as soon as this payment goes through.',
      },
    });
  };

  const createApp = async (container: ContainerType) => {
    dispatch(startCreatingApp());

    let appId: string;

    try {
      if (source === 'DOCKER_HUB') {
        // No branch, no root directory, no build method: this route brings a
        // finished artifact instead of source.
        appId = await back4app2.createAppFromImage(
          containerImage.provider,
          container.name,
          containerImage.repository,
          containerImage.tag,
          container.envVars,
          container.healthCheckEndpoint,
          container.exposedPort,
          // Sent only as a complete pair; the form will not resolve an image
          // with half a credential, so this cannot be half-filled here.
          containerImage.username || undefined,
          containerImage.password || undefined,
          user.maxApps,
          selectedPlanId,
          selectedFeatureIds,
          container.databaseUrlEnvKey
        );
      } else {
        appId = await back4app2.createAppFromRepository(container.name, container.selectedRepo!.id, container.branch, container.rootDirPath, container.autoDeploy, container.envVars, container.healthCheckEndpoint, container.exposedPort, user.maxApps, container.startCommand, container.builderPreference, container.isStaticSite, container.runtimeVersion, selectedPlanId, selectedFeatureIds, container.databaseUrlEnvKey);
      }
    } catch (e) {
      if (e instanceof NetworkError) {
        console.error('network error', e);
        dispatch(finishCreatingApp('Network error when creating app. Check your internet connection.'));
      } else if (e instanceof AuthenticationError || e instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else if (e instanceof DuplicatedError) {
        dispatch(finishCreatingApp('You already have an app with this same name.'));
      } else if (e instanceof NotFoundError) {
        dispatch(finishCreatingApp(source === 'DOCKER_HUB'
          ? 'The image was not found. Check the repository and tag and try again.'
          : 'The GitHub repository was not found. Please try to select the app repository again.'));
      } else if (e instanceof BlockedResourceError) {
        dispatch(finishCreatingApp(e.message));
      } else if (e instanceof UserVerificationPendingError) {
        dispatch(finishCreatingApp('Please verify your email to continue creating new apps.'));
      } else if (e instanceof InvalidInputError) {
        // Names the field and what to change — 'Unexpected error' does not.
        dispatch(finishCreatingApp(e.message));
      } else {
        console.error('unexpected error creating app', e);
        dispatch(finishCreatingApp('Unexpected error when creating app.'));
      }

      return;
    }

    // The app exists, on the chosen plan and pending payment. Charging is a
    // SECOND step on purpose: creating first means a failed charge leaves a
    // recoverable app instead of taking money for something that does not
    // exist. Free skips all of this.
    if (isPaid && selectedPlanId) {
      const outcome = await chargeForApp(appId, selectedPlanId);

      if (!outcome.ok) {
        // Closing the checkout is "not now", not "create it anyway". The app
        // only exists because charging needs something to charge FOR, so a
        // deliberate cancel takes it back and leaves the form exactly as it
        // was — landing on a Free app nobody asked for, on another screen, is
        // an answer to a question that was never asked.
        //
        // Only for CLOSED. A payment that FAILED is a different event: there
        // the buyer wanted the app, so it stays, on Free, and the card says
        // what happened.
        if (outcome.reason === 'CLOSED') {
          await discardApp(appId);

          return;
        }

        await fallBackToFree(appId, selectedPlanId, outcome);

        return;
      }
    }

    await settleCreatedApp(appId);
  };

  /**
   * Where a created app goes once nothing is owed on it.
   *
   * Whether its first build has started is a property of the app, not of this
   * component — so ask the app, which is also what the overview does after a
   * reload (§3.4). A failure here must not strand the customer in the wizard:
   * go to the overview, which shows the same thing.
   *
   * Reached from two places: straight through after creating, and again after
   * a payment that only succeeded on the second try.
   */
  const settleCreatedApp = async (appId: string) => {
    try {
      const app = await back4app2.findAppById(appId);

      if (app?.pendingFirstBuild) {
        dispatch(holdFirstBuild(app));

        return;
      }
    } catch (e) {
      console.error('could not read the app after creating it', e);
    }

    navigate(`/apps/${appId}`);
  };

  /**
   * Publishes the first build the analysis held, and leaves.
   *
   * There is nothing left to ask by the time this runs: the analyzer's
   * suggestions are read on the CREATE FORM now, when the repository is
   * picked, so whatever the customer wanted from them is already in the app
   * that was just created. The gate can still hold the build — it compares
   * what the model said against what was saved, and a suggestion the customer
   * declined still counts as a difference — so the hold has to be released
   * rather than assumed away.
   *
   * This is the effect that went missing when the payment step was folded into
   * this flow: without it a held first build left the wizard on its spinner
   * forever, and the app never built at all.
   */
  useEffect(() => {
    if (step === HOLD_STEP && createdApp?.pendingFirstBuild?.serviceEnvironmentId) {
      onFirstBuildRelease();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, createdApp?.id]);

  const onFirstBuildRelease = async () => {
    const serviceEnvironmentId =
      createdApp?.pendingFirstBuild?.serviceEnvironmentId;

    try {
      if (serviceEnvironmentId) {
        await back4app2.releaseFirstBuild(serviceEnvironmentId);
      }
    } catch (e) {
      // The app exists either way, and its own page offers the same release.
      // Stranding the customer on a spinner because this call failed would be
      // the worse of the two outcomes by a distance.
      console.error('could not release the first build', e);
    }

    navigate(`/apps/${createdApp!.id}`);
  };

  /**
   * Takes back an app that was created only to be paid for, when the buyer
   * closed the checkout.
   *
   * It has no deployment yet — the first build waits on the payment — so this
   * removes a row and nothing else. A failure is not worth a message: the app
   * is on Free at worst, and telling someone who just cancelled that the
   * cancel half-worked helps nobody. The form stays as it was, fields and all.
   */
  const discardApp = async (appId: string) => {
    try {
      await back4app2.deleteApp(appId);
    } catch (e) {
      console.error('could not discard the app after the checkout was closed', e);
    }

    dispatch(finishCreatingApp());
  };

  /**
   * An app is never LEFT on a plan it did not pay for.
   *
   * "Pending payment" is a state that helps nobody: the customer has an app
   * that will not run and a free slot that is spent anyway. So it lands on
   * Free — without the extras that were waiting on the payment — and the card
   * reports what happened instead of asking them to choose it.
   *
   * Still on this screen: navigating away would deliver the news on an app
   * page, out of the flow where the decision was being made.
   */
  const fallBackToFree = async (
    appId: string,
    planId: string,
    outcome: { reason: 'CLOSED' | 'FAILED'; message?: string }
  ) => {
    let movedToFree = true;
    let fallbackError: string | undefined;

    try {
      await back4app2.moveContainerAppToFree(appId);
    } catch (e) {
      // Refused, not broken: a full free allowance, or a repository that
      // cannot be on Free at all. The app stays pending, and the card says so.
      console.error('could not fall back to the free plan', e);
      movedToFree = false;
      fallbackError =
        e instanceof Error && e.message
          ? e.message
          : "We couldn't move this app to the Free plan.";
    }

    dispatch(askPaymentDecision({
      appId,
      planId,
      reason: outcome.reason,
      message: outcome.message,
      movedToFree,
      fallbackError,
    }));
  };

  /**
   * Same charge, same screen — offered only while the app is still pending,
   * which is the only state in which paying is what it needs.
   */
  const onRetryPayment = async () => {
    if (!paymentDecision) {
      return;
    }

    dispatch(startResolvingPayment());

    const outcome = await chargeForApp(paymentDecision.appId, paymentDecision.planId);

    if (!outcome.ok) {
      // Closing this one changes nothing: the app already exists and this card
      // is already showing its state. Put it back the way it was.
      if (outcome.reason === 'CLOSED') {
        dispatch(finishResolvingPayment());

        return;
      }

      // The same rule, again: a second refusal does not leave it pending
      // either. The allowance may have room now that it did not have before.
      await fallBackToFree(paymentDecision.appId, paymentDecision.planId, outcome);

      return;
    }


    dispatch(finishResolvingPayment());
    await settleCreatedApp(paymentDecision.appId);
  };

  // Only where it says something the step name does not. On the first step the
  // blurb was a restatement of the rail; here it says the two things that are
  // not obvious from a screen of answers — that they were worked out for you,
  // and that none of it is permanent.
  const stepBlurb = step === 1
    ? "We read your repository and filled everything in. Review it, change what you need — all of it is editable later in Settings."
    : '';

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  /**
   * What the app will receive because of an extra it is buying.
   *
   * Only `DATABASE_URL`, though the launch also sets the `PG*` set: this is the
   * one an app actually connects with, and listing six rows on a creation form
   * would bury the decision the screen is asking for. The app's own Settings
   * shows the full set, where there is room for it.
   *
   * Keyed on the feature's KIND, never on its name or key — the catalog is
   * editable, and a second database extra has to light this up on its own.
   */
  const managedEnvVarKeys = features.some(
    (feature) =>
      selectedFeatureIds.includes(feature.id) && feature.kind === 'database'
  )
    ? ['DATABASE_URL']
    : [];

  // The extra the form offers when the analysis finds a connection string the
  // code reads. Same action as Add in Features, including the move to a plan
  // that sells it — offered only while it is not already chosen.
  const databaseFeature = features.find((feature) => feature.kind === 'database');
  const onAddDatabase =
    databaseFeature && !selectedFeatureIds.includes(databaseFeature.id)
      ? () => dispatch(toggleFeature(databaseFeature.id))
      : undefined;

  const total = containerTotal(selectedPlan, features, selectedFeatureIds);
  const isPaid = total > 0;



  // Only while a FREE plan is the chosen one: a paid app does not consume a
  // free slot, so the allowance has nothing to say about it.
  const isFreeAllowanceSpent =
    !!selectedPlan &&
    selectedPlan.price === 0 &&
    typeof creationLimits?.maxFreeApps === 'number' &&
    creationLimits.freeAppsUsed >= creationLimits.maxFreeApps;

  const showContent = (step: number) => {
    switch (step) {
      case 0:
        // Tab bar, then a body that is only the active source's work — the
        // shape the design system uses for a composed screen. The repository
        // list is deliberately full-bleed (padding lives on each row), so the
        // image form supplies its own; `newContainerWrapper` has none.
        return <>
          {/* The question, the two cards and the search now live together in
              the panel's own head. They were split across the card header and
              the body before, which is why the search box had to be kept
              mounted on both sources just to stop the header reflowing — it
              is one box in one place now, and the reflow problem is gone. */}
          {/* The head is a block on the page; the card below it holds the two
              source options, the search that filters the list, and the list —
              which is the one thing the step is really about. */}
          <div className={styles.newContainerWrapper}>
            <div className="px-4 lg:px-5 py-4 lg:py-5 border-b border-navy-700">
              <SourceSelector value={source} onChange={setSource} githubConnected={!!gitHubRepositories?.length} />

              {/* Hidden only where it can do nothing: GitHub with no
                  repositories at all has nothing to filter, and an empty box
                  there reads as a broken search rather than as an account with
                  no access yet. */}
              {source === 'DOCKER_HUB' || gitHubRepositories?.length ? (
                <div className="mt-4 flex items-center gap-2.5 h-[38px] px-3 rounded-control bg-navy-800 border border-navy-400 focus-within:border-action focus-within:shadow-[0_0_0_3px_rgba(32,138,236,0.25)] transition-colors">
                  <SearchIcon className="flex-none opacity-70" />
                  <input
                    className="flex-1 min-w-0 bg-transparent border-0 outline-none text-white text-[16px] lg:text-ds-135 placeholder:text-navy-300"
                    placeholder={
                      isNarrowScreen
                        ? source === 'DOCKER_HUB' ? 'Search Docker Hub' : 'Search repositories'
                        : source === 'DOCKER_HUB' ? 'Search images on Docker Hub — e.g. nginx, acme/api' : 'Search your GitHub repositories'
                    }
                    value={searchInputText}
                    onChange={(e) => setSearchInputText(e.target.value)}
                  />
                  {/* Only for the list this component owns. The Docker Hub
                      count lives inside ContainerImageSource, which is what
                      performs that search — reporting it from here would mean
                      lifting the result set out of the component that fetches
                      it, to save the eye a few centimetres. */}
                  {source === 'GITHUB' && !!gitHubRepositories?.length && (
                    <span className="hidden sm:inline flex-none font-courrier text-ds-115 text-navy-300">
                      {visibleRepositoryCount} {visibleRepositoryCount === 1 ? 'result' : 'results'}
                    </span>
                  )}
                </div>
              ) : null}

              {/* At the top, where you look when the repository you want is
                  not in the list — it used to be a row at the BOTTOM of it,
                  which is the one place someone scanning for a missing name
                  never reaches. A repository owned by an organization is
                  invisible here until the GitHub App is granted access to it,
                  and that grant happens on GitHub, not on this screen. */}
              {source === 'GITHUB' && !!gitHubRepositories?.length ? (
                <p className="m-0 mt-2.5 text-ds-115 leading-[1.5] text-navy-300">
                  Missing one?{' '}
                  <span className="hidden sm:inline">
                    Repositories owned by an organization appear here only after
                    you grant access to them.{' '}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      trackEvent(AmplitudeEvent.EDIT_GITHUB_PERMISSION_CLICK);
                      openGithubPermissions();
                    }}
                    className="b4-focus text-action-soft hover:underline"
                  >
                    Edit GitHub permissions
                  </button>
                </p>
              ) : null}
            </div>

            {/* Keyed on the source so switching replays the enter animation —
                the body arrives with the cards instead of snapping ahead. */}
            <div
              key={source}
              id="source-panel"
              role="tabpanel"
              className="source-panel-enter"
              aria-labelledby={source === 'GITHUB' ? 'source-tab-github' : 'source-tab-image'}
            >
              {source === 'GITHUB'
                ? <IntegrateGithub
                    githubRepos={gitHubRepositories}
                    isLoadingRepository={isLoadingRepository}
                    isImporting={isImporting}
                    errorMsg={importingRepoError || loadingRepositoryErrorMsg}
                    onImport={onImportRepository}
                    filterText={searchInputText}
                    openRepositoryId={selectedRepository?.id}
                    openSlot={selectedRepository ? (
                      <ConfirmRepository
                        repository={selectedRepository}
                        onBack={() => dispatch(clearSelectedRepository())}
                        onContinue={(branchName, rootDirectoryPath) =>
                          dispatch(confirmRepository(branchName, rootDirectoryPath))}
                      />
                    ) : undefined}
                  />
                : <ContainerImageSource
                    value={containerImage}
                    onChange={setContainerImage}
                    onNext={() => dispatch({ type: ContainerFlowActionType.SetCurrentStep, payload: 1 })}
                    searchQuery={searchInputText || ''}
                    onResolved={() => {}}
                  />}
            </div>
          </div>
        </>
      case 1:
        // One step, one review, one button. The settings on the left, what it
        // costs on the right — see the note on HOLD_STEP for why the plan is
        // no longer a screen of its own.
        return (
          <CreateContainerForm
            isCreatingApp={isCreatingApp}
            creatingAppErrorMessage={creatingAppErrorMessage}
            onCancel={onClickBack}
            onSubmit={onContainerFormSubmit}
            selectedRepository={selectedRepository}
            initialBranchName={repositoryBranch}
            initialRootDirectoryPath={repositoryRootDir}
            source={source}
            takenNames={takenAppNames}
            // The PLAN decides this, not the total: an extra bought alongside
            // Free would make the total non-zero without buying auto deploy.
            autoDeployAvailable={!!selectedPlan && selectedPlan.price > 0}
            // Auto deploy is a paid-plan feature, and turning it on while Free
            // is chosen picks the cheapest paid plan rather than refusing.
            paidPlanName={cheapestPaidPlan(plans)?.name}
            onRequirePaidPlan={() => {
              const plan = cheapestPaidPlan(plans);
              if (plan) {
                dispatch(selectPlan(plan.id));
              }
            }}
            managedEnvVarKeys={managedEnvVarKeys}
            onAddDatabase={onAddDatabase}
            databasePrice={databaseFeature?.price}
            imageReference={
              containerImage.repository
                ? `${containerImage.repository}:${containerImage.tag || 'latest'}`
                : undefined
            }
            // One line under the button, and only what the total does not
            // already say: whether money moves, and when. The amount itself is
            // right above it, in the rail.
            footerTitle={
              isLoadingCatalog
                ? 'Loading plans…'
                : isPaid
                ? `Charged ${formatMoney(total)} now, then monthly.`
                : 'No charge.'
            }
            footerSubtitle={
              isLoadingCatalog
                ? undefined
                : isPaid
                ? // No card named here, and it was tried. Paddle saves a card
                  // AGAINST THE SUBSCRIPTION THAT CREATED IT
                  // (`origin: subscription_saved_during_purchase`), not as a
                  // wallet for the account — so a new app's checkout does not
                  // offer it, and three consecutive purchases were each typed
                  // in by hand. Naming the card on the way to a form that will
                  // ask for one anyway is a promise this screen cannot keep.
                  'Cancel anytime.'
                : 'You can upgrade later in Settings.'
            }
            // The price is on the button only for a paid app. The total sits
            // directly above it either way, so a second copy on a free app
            // would be the number $0.00 said twice in one glance.
            //
            // Never "Pay": a new app has no subscription yet, so the click
            // creates the app and opens the checkout — it does not charge.
            submitLabel={isPaid ? `Create app · ${formatMoney(total)}/mo` : 'Create app'}
            submitVariant={isPaid ? 'primary' : 'action'}
            // No plan means no informed charge. The catalog is still loading or
            // failed to load, and either way there is nothing honest to bill.
            // A spent free allowance blocks the same button, because the server
            // would refuse anyway — better to say so before the click than
            // after it.
            isSubmitDisabled={!selectedPlan || isFreeAllowanceSpent}
            rail={
              <InstanceSizePanel
                plans={plans}
                features={features}
                selectedPlanId={selectedPlanId}
                selectedFeatureIds={selectedFeatureIds}
                onSelectPlan={(planId) => dispatch(selectPlan(planId))}
                isLoading={isLoadingCatalog}
                errorMessage={catalogErrorMessage}
                freeAppsUsed={creationLimits?.freeAppsUsed}
                maxFreeApps={creationLimits?.maxFreeApps}
              />
            }
            extras={
              <FeaturesCard
                features={features}
                plans={plans}
                selectedPlanId={selectedPlanId}
                selectedFeatureIds={selectedFeatureIds}
                onToggleFeature={(featureId) => dispatch(toggleFeature(featureId))}
                isLoading={isLoadingCatalog}
              />
            }
          />
        );
      case HOLD_STEP:
        // Not a step: the app already exists by the time this renders, and
        // there is nothing left to ask. It was a third step that reviewed what
        // the analyzer had worked out and offered to change it — with a 90
        // second timer that took the defaults anyway, which is what almost
        // everyone let it do. Now it takes them straight away and says so
        // while the build starts.
        return (
          <div className="rounded-card bg-navy-800 px-6 py-16 flex flex-col items-center text-center gap-4">
            <span className="w-11 h-11 flex-none rounded-full bg-action/[0.14] text-action-soft flex items-center justify-center">
              <Spinner className="animate-spin" width="20px" height="20px" />
            </span>
            <div className="flex flex-col items-center gap-2">
              <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
                Creating {createdApp?.name || 'your app'}
              </span>
              <p className="m-0 max-w-[46ch] text-ds-135 leading-[1.55] text-navy-200">
                We're setting it up and starting the first build. You'll land on
                the app in a moment.
              </p>
            </div>
          </div>
        )
      default:
        return <div>Ooops!! sorry 😜</div>
    }
  }

  if (isImporting || (isImportingSuccess) || importingRepoError) {
    let content;
    if (isImportingSuccess) {
      content = <div className="flex h-full justify-center items-start flex-col">
        <h1 className="font-sora text-3xl text-center mb-4">Github Installation Completed</h1>
        <div className="font-inter text-sm text-left">This window will close automatically.</div>
      </div>
    } else if (importingRepoError) {
      content = <div className="flex h-full justify-center items-start flex-col">
        <h1 className="font-sora text-3xl mb-4">{importingRepoError}</h1>
        <div className="font-inter text-sm">This window will close automatically in 8 seconds.</div>
      </div>
      if (showCreditCardRequired) {
        content = <div className="flex h-full justify-center items-start flex-col pl-4">
          <h1 className="font-sora text-3xl mb-4">Please validate your credit card to proceed.</h1>
          <div className="w-full">
            <Button type='primary' onClick={() => window.close()}><a role="button" target="_blank" rel='noreferrer noopener' className='font-semibold text-sm' href={`https://checkout.back4app.io/subscription/0EeFjudf6H`}>Validate Now</a></Button>
            <Button type='secondary' onClick={() => window.close()} className='text-sm border-[#fafafa33] font-medium ml-4'>Cancel</Button>
          </div>
        </div>
      }
    } else {
      content = <div className="flex h-full justify-center items-center flex-col">
        <div className="flex justify-center items-center overflow-visible h-6 mb-4 z-[-1]">
          <GithubIcon width="24px" height="24px" />
          <div className="flex space-x-2 ml-5">
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-dot-flashing"></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_0.5s_alternate] "></span>
            <span className="w-1 h-1 rounded bg-[#D9D9D9] animate-[dot-flashing_1s_infinite_1s_alternate] "></span>
          </div>
          <B4AIconSVG width="76px" height="76px" />
        </div>
        <div className="font-sora font-semibold text-lg leading-140 text-center">Importing repositories...</div>
      </div>
    }
    return <div className="absolute w-full h-full top-0 left-0 bottom-0 right-0 bg-navy-800 z-20 p-4">
      {content}
    </div>
  }
  // What the stepper shows under each label, once the choice behind it exists.
  // Written here rather than in the stepper so that stays a dumb renderer and
  // every string describing this flow lives next to the state it reads.
  // Same predicate the list itself runs, imported rather than repeated.
  const visibleRepositoryCount = filterRepositories(gitHubRepositories || [], searchInputText).length;

  return <div ref={scrollContainerRef} className='flex-1 min-h-0 relative overflow-y-auto b4-thin bg-navy-900'>
    <div className={styles.container}>
      {/* One head for the flow: what this is, what you are doing, and what
          this particular screen wants. It replaces the step header — a bar,
          a count and a second heading — which said in three parts what the
          third line says in a sentence, and said it in a second voice the
          same size as the first.

          Not `lg` only: this is the only head there is now, so a phone gets
          it too. */}
      <div className="flex flex-col gap-2 mb-8 lg:mb-10">
        {/* A way OUT of the step, so it sits above the lines that describe the
            step. Hidden on the hold screen, which has nowhere to go back to:
            the app exists by then, and the configure step's submit would
            create a second. */}
        {step < HOLD_STEP ? (
          <button
            type="button"
            onClick={onClickBack}
            className="b4-focus self-start mb-1 -ml-2.5 h-[30px] px-2.5 rounded-control inline-flex items-center gap-1.5 text-[length:calc(13px*var(--b4-type,1))] font-medium text-navy-200 hover:text-white hover:bg-navy-600 transition-colors duration-[120ms]"
          >
            <ArrowLeftIcon size={14} />
            Back
          </button>
        ) : null}

        <Eyebrow>web deployment</Eyebrow>
        <h1 className={styles.headerText}>Deploy a web app</h1>

        {stepBlurb ? (
          <p className="m-0 mt-1 text-ds-135 leading-[1.5] text-navy-200">
            {/* A phone gets the half that matters: three lines above the first
                card push the review under the fold. */}
            <span className="sm:hidden">We filled everything in. Review it — you can change it later.</span>
            <span className="hidden sm:inline">{stepBlurb}</span>
          </p>
        ) : null}
      </div>

      {/* One line for the whole flow, instead of one per step. The rail now
          says which step you are on, so re-stating it here was the same
          information twice — and the step-specific wording was what made the
          heading and the sentence under it disagree on the image route. */}

      {/* A column of blocks, not one big box: the head sits on the page, and
          each step brings whatever cards it needs under it. */}
      <div className={`flex flex-col gap-4 lg:gap-6 ${step === HOLD_STEP ? 'min-h-[300px]' : ''} mb-16 lg:mb-12`}>
          {/* Replaces the step, rather than sitting next to it: the app already
              exists, so the form that created it would create a SECOND one. */}
          {paymentDecision ? (
            <div className="rounded-card bg-navy-800 border border-navy-400 px-6 py-8 lg:px-8 lg:py-10 flex flex-col gap-5">
              {/* Two different pieces of news, and only one of them is bad.
                  A closed checkout that landed the app on Free is not an
                  error — nothing is broken, nothing is owed, and the app is
                  building. Dressing it in a warning would say otherwise. */}
              <div className="flex items-start gap-4">
                <span
                  className={
                    'w-11 h-11 flex-none rounded-full flex items-center justify-center ' +
                    (paymentDecision.movedToFree
                      ? 'bg-action/[0.14] text-action-soft'
                      : 'bg-state-warn/[0.14] text-state-warn')
                  }
                >
                  <AlertIcon size={22} />
                </span>
                <div className="min-w-0">
                  <h2 className="m-0 font-sora text-ds-15 text-white">
                    {paymentDecision.movedToFree
                      ? 'Created on the Free plan'
                      : paymentDecision.reason === 'FAILED'
                      ? "We couldn't complete the payment"
                      : 'Payment not completed'}
                  </h2>
                  {!paymentDecision.movedToFree ? (
                    /* The gateway's sentence when it gave one; otherwise the
                       plain fact, which is all a closed checkout is. */
                    <p className="m-0 mt-1.5 text-ds-135 leading-[1.5] text-navy-200">
                      {paymentDecision.message ||
                        'You closed the checkout before it went through.'}
                    </p>
                  ) : null}
                </div>
              </div>

              {paymentDecision.movedToFree ? (
                <>
                  {/* Not an offer — a statement. The app is already there. */}
                  <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">
                    {paymentDecision.reason === 'FAILED'
                      ? 'The payment did not go through, so we created your app on the '
                      : 'You closed the checkout, so we created your app on the '}
                    <strong className="text-white font-medium">Free</strong> plan
                    {selectedFeatureIds.length ? ', without the extras you picked' : ''}.
                    It is deploying now, and you can move it to a paid plan any
                    time from its Plan page.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <DsButton
                      variant="primary"
                      onClick={() => navigate(`/apps/${paymentDecision.appId}`)}
                    >
                      Go to your app
                    </DsButton>
                  </div>
                </>
              ) : (
                <>
                  {/* The fallback was refused, so the app really is stuck on a
                      plan it has not paid for, and paying is the only way out
                      of it. Say why, and keep the door open. */}
                  <p className="m-0 text-ds-135 leading-[1.5] text-state-err">
                    {paymentDecision.fallbackError}
                  </p>
                  <p className="m-0 text-ds-135 leading-[1.5] text-navy-200">
                    Your app is created and waiting on the payment.
                  </p>

                  {paymentDecisionError ? (
                    <p className="m-0 text-ds-135 leading-[1.5] text-state-err">{paymentDecisionError}</p>
                  ) : null}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <DsButton
                      variant="primary"
                      onClick={onRetryPayment}
                      isDisabled={isResolvingPayment}
                    >
                      {isResolvingPayment ? 'Working…' : `Try again — ${formatMoney(total)}`}
                    </DsButton>
                    <DsButton
                      variant="outline"
                      onClick={() => navigate(`/apps/${paymentDecision.appId}`)}
                      isDisabled={isResolvingPayment}
                    >
                      Go to your app
                    </DsButton>
                  </div>
                </>
              )}
            </div>
          ) : (
            showContent(step)
          )}
      </div>
    </div>

    {checkoutModal}
  </div>;
};

export default NewContainerFlow;
